/* ==========================================================
   Youth Learner — Static course + preview data
   All content 4th-grade reading level.
   ========================================================== */

/**
 * Node kinds:
 *  - 'story'   : Reading only, no hearts, may include a two-card choice.
 *  - 'math'    : Reading + tap-to-reveal reward, no hearts.
 *  - 'cards'   : Interactive tap-to-flip / tap-to-expand cards.
 *  - 'match'   : Tap left → tap right activity, no hearts on match itself.
 *  - 'loop'    : Sense–Plan–Act animated diagram.
 *  - 'placement': Two-good-options choice (both correct), earns XP either way.
 *  - 'repair'  : Ordered tap sequence (Power → Actuators → Sensors → Controller).
 *  - 'celebration': Final celebration lesson before final quiz.
 *
 * Every node ends with a `quiz` array (0–5 questions). Empty quiz means
 * completion is granted after the story/activity itself.
 */

export const SOLAR_COURSE = {
  id: 'solar',
  title: 'The Solar Power Adventure',
  emoji: '☀️',
  badge: { name: 'Solar Pioneer', color: '#f59e0b', icon: '☀️' },
  credential: 'Clean Energy Foundations',
  estimateMin: 28,
  xpAvailable: 425,
  hearts: true,
  nodes: [
    {
      id: 'cabin',
      title: 'The Cabin Problem',
      emoji: '🏠',
      kind: 'story',
      illustration: { emoji: '🌲🏚️🌌', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 60%,#081827 100%)' },
      body: [
        'Your family just moved to a cabin in the woods.',
        'The cabin is cozy and quiet. But there is one problem.',
        'There are no power lines out here. No electricity means no lights, no fridge, and no TV!',
        'You need to find a way to get power to your cabin. What do you do?',
      ],
      choice: {
        prompt: 'What do you do?',
        options: [
          {
            id: 'a',
            title: 'Ask the power company to run a line through the woods',
            icon: 'bolt',
            correct: false,
            consequence: {
              icon: '💰',
              text: 'Oops! The power company says it will cost over $100,000 to run a line through the woods. That\'s way too much money!',
              retry: 'Go back and try solar power',
            },
          },
          {
            id: 'b',
            title: 'Look into solar power instead',
            icon: 'sun',
            correct: true,
            cheer: 'Smart choice!',
          },
        ],
      },
      quiz: [
        {
          q: 'Why is solar power a good choice for a cabin in the woods?',
          options: [
            { t: 'It is the most expensive option', c: false },
            { t: 'You can make your own electricity right at home', c: true },
            { t: 'Power lines are faster to build', c: false },
            { t: 'Solar only works in sunny countries', c: false },
          ],
          explanation: 'Solar panels make electricity from sunlight — right where you live! No need for power lines.',
        },
      ],
    },
    {
      id: 'power-need',
      title: 'How Much Power Do You Need?',
      emoji: '📊',
      kind: 'math',
      illustration: { emoji: '🔦📺❄️', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      body: [
        'Before you buy solar panels, you need to figure something out. How much power does your cabin use each day?',
        'This is important! If you get too few panels, your lights will go out at night.',
        'Every appliance in your home uses watts. Watts measure how much power something needs.',
      ],
      stats: [
        { icon: '🔦', label: 'Lights', value: '100 watts' },
        { icon: '📺', label: 'TV', value: '150 watts' },
        { icon: '❄️', label: 'Fridge', value: '200 watts' },
      ],
      totalLine: 'Total per week: 60 kWh',
      math: {
        title: 'The Safety Buffer Math Challenge! 🧮',
        intro: 'Smart solar planners add extra panels for cloudy days. We call this a safety buffer. You add 25% more to be safe.',
        equation: '60 kWh × 1.25 = ?',
        answer: '75',
        answerFull: '= 75 kWh per week!',
        explanation: '75 kWh is how much energy your panels need to make each week — even on cloudy days!',
      },
      quiz: [
        {
          q: 'Why do we add a safety buffer?',
          options: [
            { t: 'To make panels more expensive', c: false },
            { t: 'So we have extra power for cloudy days', c: true },
            { t: 'Because all cabins need 75 kWh', c: false },
          ],
        },
        {
          q: 'If your cabin uses 80 kWh per week, what is 80 × 1.25?',
          options: [
            { t: '100 kWh', c: true },
            { t: '105 kWh', c: false },
            { t: '95 kWh', c: false },
          ],
          explanation: 'Show the math: 80 × 1.25 = 100.',
        },
      ],
    },
    {
      id: 'pick-panels',
      title: 'Picking Your Solar Panels',
      emoji: '☀️',
      kind: 'cards',
      illustration: { emoji: '🖤💙', bg: 'linear-gradient(160deg,#1e3a5f 0%,#0f2744 100%)' },
      subtitle: 'Two Kinds of Panels!',
      cards: [
        {
          title: 'Monocrystalline Panels 🖤',
          bullets: [
            'More power in a small space',
            'Better when you have shade',
            'Costs a little more',
            'Best for: Small roofs with trees nearby',
          ],
        },
        {
          title: 'Polycrystalline Panels 💙',
          bullets: [
            'Costs less money',
            'Needs more roof space',
            'Not as good in shade',
            'Best for: Big open roofs',
          ],
        },
      ],
      body: [
        'Your cabin has a SMALL roof. And there are big trees nearby that make shade.',
        'Which panel is the better pick for YOUR cabin?',
      ],
      choice: {
        prompt: 'Which panel do you pick?',
        options: [
          {
            id: 'poly',
            title: 'Polycrystalline — cheaper is better!',
            correct: false,
            consequence: {
              icon: '😢',
              text: 'Uh oh! You need to cut down three big trees to fit enough panels. Your yard looks bare now.',
              retry: 'Go back and try monocrystalline',
            },
          },
          {
            id: 'mono',
            title: 'Monocrystalline — made for small spaces!',
            correct: true,
            cheer: 'Great thinking!',
          },
        ],
      },
      quiz: [
        {
          q: 'Which panel is better for a small, shady roof?',
          options: [
            { t: 'Polycrystalline', c: false },
            { t: 'Monocrystalline', c: true },
          ],
        },
        {
          q: 'Why might someone pick polycrystalline panels?',
          options: [
            { t: 'They work better in shade', c: false },
            { t: 'They cost less money', c: true },
            { t: 'They are smaller', c: false },
          ],
        },
        {
          q: 'What does a "safety buffer" protect you from?',
          options: [
            { t: 'Cloudy days when panels make less power', c: true },
            { t: 'Trees falling on your roof', c: false },
            { t: 'The power company charging fees', c: false },
          ],
        },
      ],
      teaser: { name: 'Panel Pro', locked: true },
    },
    {
      id: 'placement',
      title: 'Where to Put Your Panels',
      emoji: '🔧',
      kind: 'placement',
      illustration: { emoji: '🏠🌿', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Two Great Options!',
      body: [
        'Both options work great! Solar experts say both can fill your batteries every day.',
        'You get to choose based on your cabin!',
      ],
      cards: [
        {
          title: 'Roof Mount 🏠',
          bullets: [
            'Panels go on top of your cabin roof',
            'Out of the way',
            'Can catch sun through tree gaps',
          ],
          success: 'Your roof catches sunlight through the trees. Batteries fill every day! ☀️',
        },
        {
          title: 'Pole Mount in the Yard 🌿',
          bullets: [
            'Panels on a pole in the yard',
            'Easy to clean and adjust',
            'Runs a little cooler than roof panels',
          ],
          success: 'Your pole panels stay cool and clean. Easy to angle toward the sun! ☀️',
        },
      ],
      quiz: [
        {
          q: 'What is one advantage of pole-mounted panels?',
          options: [
            { t: 'They make more electricity', c: false },
            { t: 'They are easy to clean and adjust', c: true },
            { t: 'They work without sunlight', c: false },
          ],
        },
        {
          q: 'Which is TRUE about rooftop panels?',
          options: [
            { t: 'They never work when there are trees', c: false },
            { t: 'They can catch sun through gaps in trees', c: true },
            { t: 'They cost less than pole panels', c: false },
          ],
        },
      ],
    },
    {
      id: 'solar-home',
      title: 'Your Solar Home!',
      emoji: '🏆',
      kind: 'celebration',
      illustration: { emoji: '🏡☀️🌳', bg: 'linear-gradient(160deg,#f59e0b 0%,#f97316 100%)' },
      body: [
        'You did it! Your cabin now runs on sunshine.',
        'You figured out how much power you need. You picked the right panels.',
        'You mounted them in the perfect spot. Your batteries fill up every single day — even on cloudy weeks!',
      ],
      quiz: [
        {
          q: 'What formula do we use to find how much energy you need?',
          options: [
            { t: 'Watts × Hours ÷ 1,000 = kWh', c: true },
            { t: 'Watts + Hours + 1,000 = kWh', c: false },
            { t: 'Hours ÷ Watts × 1,000 = kWh', c: false },
          ],
        },
        {
          q: 'Why do we add a 25% safety buffer?',
          options: [
            { t: 'All solar experts require it', c: false },
            { t: 'To have extra power for cloudy days', c: true },
            { t: 'To make the math easier', c: false },
          ],
        },
        {
          q: 'Which panel makes more power in a small shady space?',
          options: [
            { t: 'Polycrystalline', c: false },
            { t: 'Monocrystalline', c: true },
          ],
        },
        {
          q: 'True or False: Pole-mounted panels can be tilted toward the sun.',
          options: [
            { t: 'True', c: true },
            { t: 'False', c: false },
          ],
        },
      ],
    },
  ],
};

export const ROBOT_COURSE = {
  id: 'robot',
  title: 'Robot Rescue! An AI & Robotics Adventure',
  emoji: '🤖',
  badge: { name: 'Future Engineer', color: '#60a5fa', icon: '🤖' },
  credential: 'AI & Technology Foundations',
  estimateMin: 32,
  xpAvailable: 500,
  hearts: true,
  nodes: [
    {
      id: 'parts',
      title: 'What is a Robot?',
      emoji: '🤖',
      kind: 'cards',
      illustration: { emoji: '🐕🤖', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Every Robot Has 4 Parts!',
      body: [
        'Meet Sparky! Sparky is a robot dog. He can walk, play, and even talk.',
        'But how does he work?',
        'Every robot — from a robot dog to a self-driving car — has 4 main parts.',
        'If ONE part breaks, the robot stops working! Let\'s learn all four parts.',
      ],
      cards: [
        {
          title: 'SENSORS 🟠',
          front: '👁️',
          bullets: [
            'Sensors are like eyes and ears',
            'They tell the robot what is around it',
            'Real examples: camera, microphone, bump sensor, cliff sensor',
          ],
        },
        {
          title: 'CONTROLLER 🔵',
          front: '🧠',
          bullets: [
            'The controller is the robot\'s brain',
            'It reads what the sensors say',
            'Then it decides what to do next',
          ],
        },
        {
          title: 'ACTUATORS 🟢',
          front: '💪',
          bullets: [
            'Actuators are like muscles',
            'They make the robot MOVE!',
            'Motors, wheels, arms, even speakers',
          ],
        },
        {
          title: 'POWER ❤️',
          front: '🔋',
          bullets: [
            'Power is like the robot\'s heart',
            'It gives energy to everything',
            'Most robots use a rechargeable battery',
          ],
        },
      ],
      quiz: [
        {
          q: 'Which robot part is like its EYES?',
          options: [
            { t: 'Controller', c: false },
            { t: 'Sensor', c: true },
            { t: 'Actuator', c: false },
            { t: 'Battery', c: false },
          ],
        },
        {
          q: 'The controller is like the robot\'s ___',
          options: [
            { t: 'Muscles', c: false },
            { t: 'Heart', c: false },
            { t: 'Brain', c: true },
            { t: 'Eyes', c: false },
          ],
        },
        {
          q: 'Which part makes the robot MOVE?',
          options: [
            { t: 'Sensor', c: false },
            { t: 'Battery', c: false },
            { t: 'Controller', c: false },
            { t: 'Actuator', c: true },
          ],
        },
        {
          q: 'What gives energy to ALL robot parts?',
          options: [
            { t: 'The sensor', c: false },
            { t: 'The power/battery', c: true },
            { t: 'The controller', c: false },
            { t: 'The screen', c: false },
          ],
        },
      ],
    },
    {
      id: 'sensors',
      title: 'How Robots See and Feel',
      emoji: '👁️',
      kind: 'match',
      illustration: { emoji: '📷🤜👀', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Sensors: The Robot\'s Eyes and Ears!',
      body: [
        'Marcus noticed something strange. Sparky kept bumping into the couch!',
        'He used to avoid it just fine. Maybe one of Sparky\'s sensors stopped working.',
        'Let\'s learn what sensors do!',
      ],
      cards: [
        {
          title: 'Cliff Sensor 👀',
          bullets: [
            'Looks DOWN at the floor',
            'Shoots a tiny beam of light',
            'If light doesn\'t bounce back — STOP! Drop ahead!',
          ],
        },
        {
          title: 'Bump Sensor 🤜',
          bullets: [
            'Like a robot\'s skin',
            'Feels when the robot hits something',
            'Sends: WALL DETECTED! TURN AROUND!',
          ],
        },
        {
          title: 'Camera Sensor 📷',
          bullets: [
            'Helps the robot SEE',
            'Recognizes faces and furniture',
            'Finds its way around a room',
          ],
        },
      ],
      match: {
        title: 'Sensor Match Game!',
        pairs: [
          { left: 'A wall in front', right: 'Bump sensor' },
          { left: 'The edge of a stair', right: 'Cliff sensor' },
          { left: 'A voice command', right: 'Microphone' },
        ],
      },
      quiz: [
        {
          q: 'Which sensor stops a robot from falling down stairs?',
          options: [
            { t: 'Camera', c: false },
            { t: 'Microphone', c: false },
            { t: 'Cliff sensor', c: true },
          ],
        },
        {
          q: 'A bump sensor helps a robot detect ___',
          options: [
            { t: 'Voices', c: false },
            { t: 'Walls and objects', c: true },
            { t: 'Floor edges', c: false },
          ],
        },
        {
          q: 'Marcus\'s robot keeps bumping into things. Which part should he check first?',
          options: [
            { t: 'Battery', c: false },
            { t: 'Sensors', c: true },
            { t: 'Wheels', c: false },
          ],
        },
      ],
    },
    {
      id: 'controller',
      title: 'The Robot\'s Brain',
      emoji: '🧠',
      kind: 'loop',
      illustration: { emoji: '👁️🧠⚡', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'The Controller: Think, Decide, Act!',
      body: [
        'Marcus checked the sensors on Sparky. They looked okay. So he checked the brain — the controller.',
        'The controller does three things: SENSE, PLAN, and ACT.',
        'This is called the Sense-Plan-Act Loop! It happens hundreds of times every second!',
      ],
      loop: [
        { label: 'SENSE', icon: '👁️', color: '#f97316', text: 'Read what the sensors say' },
        { label: 'PLAN', icon: '🧠', color: '#0f2744', text: 'Decide what to do' },
        { label: 'ACT', icon: '⚡', color: '#15803d', text: 'Tell the muscles to move' },
      ],
      quiz: [
        {
          q: 'What does the controller DO?',
          options: [
            { t: 'Only moves the wheels', c: false },
            { t: 'Only reads sensors', c: false },
            { t: 'Reads sensors, makes plans, and sends commands', c: true },
          ],
        },
        {
          q: 'Put the loop in the RIGHT order:',
          options: [
            { t: 'Plan → Sense → Act', c: false },
            { t: 'Sense → Plan → Act', c: true },
            { t: 'Act → Plan → Sense', c: false },
          ],
        },
        {
          q: 'A robot runs into a glass door. Its sensors can\'t see glass. Which step of the loop failed?',
          options: [
            { t: 'Plan — the controller decided wrong', c: false },
            { t: 'Act — the wheels didn\'t turn', c: false },
            { t: 'Sense — the sensor couldn\'t detect glass', c: true },
          ],
        },
      ],
    },
    {
      id: 'actuators',
      title: 'How Robots Move',
      emoji: '⚙️',
      kind: 'cards',
      illustration: { emoji: '⚙️🔊🦾', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Actuators: The Robot\'s Muscles!',
      body: [
        'Marcus checked the brain. It seemed fine. So he looked at the ACTUATORS — the muscles.',
        'Actuators turn electricity into movement. They are the parts that actually MOVE!',
      ],
      cards: [
        {
          title: 'Wheel Motors ⚙️',
          bullets: [
            'Motors spin the wheels',
            'One motor per wheel',
            'More speed on the right wheel? The robot turns LEFT!',
          ],
        },
        {
          title: 'Speaker 🔊',
          bullets: [
            'A speaker is an actuator too!',
            'It turns electricity into SOUND',
            'When a robot says "Hello!" — that\'s an actuator doing its job!',
          ],
        },
        {
          title: 'Robot Arms 🦾',
          bullets: [
            'Bigger robots have arm motors',
            'These lift and move things',
            'Factory robots use giant arm motors to build cars',
          ],
        },
      ],
      quiz: [
        {
          q: 'Actuators are like the robot\'s ___',
          options: [
            { t: 'Brain', c: false },
            { t: 'Eyes', c: false },
            { t: 'Muscles', c: true },
          ],
        },
        {
          q: 'If both wheels spin at the SAME speed, the robot goes ___',
          options: [
            { t: 'In circles', c: false },
            { t: 'Straight', c: true },
            { t: 'Backward', c: false },
          ],
        },
        {
          q: 'Which of these is NOT an actuator?',
          options: [
            { t: 'Wheel motor', c: false },
            { t: 'Speaker', c: false },
            { t: 'Cliff sensor', c: true },
            { t: 'Robot arm', c: false },
          ],
        },
      ],
    },
    {
      id: 'fix',
      title: 'Marcus Fixes a Robot!',
      emoji: '🔧',
      kind: 'repair',
      illustration: { emoji: '🔧🤖✅', bg: 'linear-gradient(160deg,#15803d 0%,#0f2744 100%)' },
      body: [
        'Marcus worked through all 4 parts, one at a time.',
        'First he checked the POWER. The battery was fine. ✓',
        'Then he checked the ACTUATORS. The motors worked fine. ✓',
        'Then he checked the SENSORS. He found it! The bump sensor on Sparky\'s nose had a tiny crack. Dust got inside and broke it!',
        'Marcus cleaned out the dust. Sparky worked perfectly! Total repair time: 10 minutes. Total cost: $0!',
      ],
      repair: {
        title: 'Check parts in the smart order!',
        subtitle: 'Start with the easiest. Save the hardest for last.',
        order: ['Power', 'Actuators', 'Sensors', 'Controller'],
      },
      quiz: [
        {
          q: 'What is the SMART order to check robot parts?',
          options: [
            { t: 'Sensors → Power → Actuators → Controller', c: false },
            { t: 'Power → Actuators → Sensors → Controller', c: true },
            { t: 'Controller → Sensors → Actuators → Power', c: false },
          ],
        },
        {
          q: 'Marcus found that Sparky\'s sensor had dust in it. This is called ___',
          options: [
            { t: 'A software bug', c: false },
            { t: 'Sensor contamination', c: true },
            { t: 'Battery failure', c: false },
          ],
        },
        {
          q: 'Which robot part reads the world and sends info to the brain?',
          options: [
            { t: 'Actuator', c: false },
            { t: 'Controller', c: false },
            { t: 'Sensor', c: true },
          ],
        },
        {
          q: 'A robot hears a voice command, makes a plan, then moves its arm. What loop did it just run?',
          options: [
            { t: 'Sense-Plan-Act', c: true },
            { t: 'Plan-Move-Listen', c: false },
            { t: 'See-Think-Wait', c: false },
          ],
        },
        {
          q: 'Marcus checked 4 parts before finding the problem. Why not just guess?',
          options: [
            { t: 'It takes more time to check', c: false },
            { t: 'Checking first saves money and time', c: true },
            { t: 'Robots always have sensor problems', c: false },
          ],
        },
      ],
    },
  ],
};

/* Locked course previews on the path map. */
export const LOCKED_COURSES = [
  {
    id: 'maker',
    title: 'The Maker Lab',
    emoji: '🏭',
    tagline: 'Learn how things are made — from factory robots to 3D printers!',
    lessons: [
      { icon: '🔩', title: 'What is Manufacturing?' },
      { icon: '🦾', title: 'Factory Robots at Work' },
      { icon: '🖨️', title: 'The Magic of 3D Printing' },
    ],
    badge: { name: 'Maker Master', icon: '🏭' },
    career: 'Manufacturing technicians earn $18–$30/hr in North Central Texas',
    xp: 450,
    prereq: { id: 'solar', title: 'Solar Power Adventure', emoji: '☀️' },
  },
  {
    id: 'cyber',
    title: 'Digital Defender',
    emoji: '🛡️',
    tagline: 'Become a cyber superhero — protect computers from hackers and scams!',
    lessons: [
      { icon: '🕵️', title: 'How Hackers Think' },
      { icon: '🔑', title: 'Password Power-Ups' },
      { icon: '🎣', title: 'Spotting Phishing Tricks' },
    ],
    badge: { name: 'Cyber Shield', icon: '🛡️' },
    career: 'Cybersecurity specialists earn $22–$45/hr — one of the fastest-growing careers',
    xp: 450,
    prereq: { id: 'robot', title: 'Robot Rescue!', emoji: '🤖' },
  },
  {
    id: 'spark',
    title: 'Spark Academy',
    emoji: '⚡',
    tagline: 'From light switches to circuit boards — explore the world of electrical work!',
    lessons: [
      { icon: '⚡', title: 'What is Electricity, Really?' },
      { icon: '🗺️', title: 'Reading Circuit Diagrams' },
      { icon: '🔧', title: 'Wiring Things Safely' },
    ],
    badge: { name: 'Spark Pro', icon: '⚡' },
    career: 'Electrical apprentices earn $20–$30/hr while they train — paid to learn',
    xp: 500,
    prereq: { id: 'maker', title: 'The Maker Lab', emoji: '🏭' },
  },
  {
    id: 'code',
    title: 'Code Quest',
    emoji: '💻',
    tagline: 'Build real websites and apps — learn the code behind everything you click!',
    lessons: [
      { icon: '💻', title: 'What is Code and How Does It Work?' },
      { icon: '🌐', title: 'Build Your First Webpage' },
      { icon: '🎨', title: 'Make It Look Good with CSS' },
    ],
    badge: { name: 'Code Builder', icon: '💻' },
    career: 'Web developers earn $25–$50/hr — work from anywhere',
    xp: 500,
    prereq: {
      id: ['spark', 'cyber'],
      title: 'Spark Academy AND Digital Defender',
      emoji: '⚡🛡️',
    },
  },
];

export const COURSE_BY_ID = {
  solar: SOLAR_COURSE,
  robot: ROBOT_COURSE,
};

/* ==========================================================
   Courses 3–6 — full scaffolded courses (3 nodes each).
   Node 4 & 5 marked as polish pass; use existing kinds only.
   ========================================================== */

export const MAKER_COURSE = {
  id: 'maker',
  title: 'The Maker Lab',
  emoji: '🏭',
  badge: { name: 'Maker Master', color: '#f97316', icon: '🏭' },
  credential: 'Manufacturing & Technology Fundamentals',
  estimateMin: 30,
  xpAvailable: 450,
  hearts: true,
  nodes: [
    {
      id: 'manufacturing',
      title: 'What is Manufacturing?',
      emoji: '🏭',
      kind: 'cards',
      illustration: { emoji: '🏭📦🔩', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'How factories make things',
      body: [
        'Have you ever wondered how a car gets made? Or how the phone in your hand was built?',
        'Manufacturing is how we make things. Factories are buildings where things get made.',
        'Workers and machines work together to turn raw materials into finished products.',
      ],
      cards: [
        { title: 'The Assembly Line 🏭', bullets: ['Henry Ford had a big idea', 'Each worker made just ONE part', 'The car moved worker to worker', 'Now made in hours — not weeks!'] },
        { title: 'From Raw to Finished 🪵→📱', bullets: ['Start with raw materials', 'Cut, shape, or mold into parts', 'Put all the parts together', 'Test, package, and ship!'] },
        { title: 'What Gets Made? 👟', bullets: ['Your shoes, desk, and pencil', 'Every phone and tablet', 'Even the food in packages', 'If humans made it — it was manufactured!'] },
      ],
      quiz: [
        { q: 'What is manufacturing?', options: [{ t: 'Selling things in a store', c: false }, { t: 'Making things in a factory', c: true }, { t: 'Drawing plans for buildings', c: false }] },
        { q: 'What was Henry Ford\'s big idea?', options: [{ t: 'Make everything slowly by hand', c: false }, { t: 'Use an assembly line where each worker makes one part', c: true }, { t: 'Only build trucks', c: false }] },
        { q: 'Which of these is a raw material?', options: [{ t: 'A finished shoe', c: false }, { t: 'Metal', c: true }, { t: 'A completed phone', c: false }] },
      ],
    },
    {
      id: 'factory-robots',
      title: 'Factory Robots at Work',
      emoji: '🦾',
      kind: 'match',
      illustration: { emoji: '🔥🎨📦', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Robots that build our world',
      body: [
        'Today, robots help build almost everything. They work alongside humans in factories.',
        'Robots are great at doing the SAME job over and over — perfectly every time!',
        'Remember the 4 robot parts from Robot Rescue? Factory robots use the exact same ones.',
      ],
      cards: [
        { title: 'Welding Robot 🔥', bullets: ['Joins metal with intense heat', 'Too dangerous for humans', 'Never gets tired'] },
        { title: 'Painting Robot 🎨', bullets: ['Sprays paint on every car', 'The same amount every time', 'No drips or missed spots'] },
        { title: 'Conveyor Belt Robot 📦', bullets: ['Moves parts between stations', 'Never drops anything', 'Carries very heavy loads safely'] },
      ],
      match: {
        title: 'Match the Robot Job!',
        pairs: [
          { left: 'Joins metal with heat', right: 'Welding robot' },
          { left: 'Sprays evenly on every product', right: 'Painting robot' },
          { left: 'Moves heavy parts safely', right: 'Conveyor robot' },
        ],
      },
      quiz: [
        { q: 'Why are robots good at factory work?', options: [{ t: 'They take lots of breaks', c: false }, { t: 'They do the same job perfectly every time', c: true }, { t: 'They\'re smarter than humans', c: false }] },
        { q: 'Which factory robot joins metal with heat?', options: [{ t: 'Painting robot', c: false }, { t: 'Welding robot', c: true }, { t: 'Conveyor robot', c: false }] },
        { q: 'Factory robots use the same 4 parts as Sparky. What are they?', options: [{ t: 'Wheels, wings, lights, motors', c: false }, { t: 'Sensors, Controller, Actuators, Power', c: true }, { t: 'Arms, legs, eyes, voice', c: false }] },
      ],
    },
    {
      id: 'quality',
      title: 'Quality Control',
      emoji: '✅',
      kind: 'cards',
      illustration: { emoji: '🔍✅❌', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Check every part!',
      body: [
        'Imagine buying a brand new phone. You open the box and the screen is cracked. Or the buttons don\'t work.',
        'Quality control is how factories make sure every product works perfectly BEFORE it reaches you.',
        'Every product is tested and inspected. Only perfect products get shipped to stores!',
      ],
      cards: [
        { title: 'Inspection Stations 🔍', bullets: ['Every product is tested', 'Broken ones go back to be fixed', 'Only perfect ones ship out'] },
        { title: 'Why It Matters to You 📱', bullets: ['You get a product that works', 'Fewer returns and refunds', 'Trust in the brand grows'] },
        { title: 'Real-World Standards 📏', bullets: ['ISO 9001 — the global rulebook', 'Six Sigma — 3.4 defects per million!', 'Automotive & medical = strictest of all'] },
      ],
      quiz: [
        { q: 'What is the goal of quality control?', options: [{ t: 'Make products as fast as possible', c: false }, { t: 'Make sure every product works perfectly', c: true }, { t: 'Save money by skipping inspections', c: false }] },
        { q: 'A factory makes 1,000 toy cars. 5 have broken wheels. What should happen?', options: [{ t: 'Ship them anyway', c: false }, { t: 'Throw them all away', c: false }, { t: 'Fix the broken ones before shipping', c: true }] },
        { q: 'Why is quality control important to YOU?', options: [{ t: 'Products cost more money', c: false }, { t: 'So the product you buy actually works', c: true }, { t: 'Factories make fewer things', c: false }] },
      ],
    },
    {
      id: 'big-build',
      title: 'The Big Build Challenge!',
      emoji: '🏆',
      kind: 'celebration',
      illustration: { emoji: '🏭🏆⚙️', bg: 'linear-gradient(160deg,#f59e0b 0%,#f97316 100%)' },
      subtitle: 'Put it all together',
      body: [
        'You\'ve learned everything about manufacturing! Assembly lines. Factory robots. 3D printing. Quality control.',
        'Now it\'s time for the BIG BUILD CHALLENGE! Can you put it all together?',
      ],
      quiz: [
        { q: 'Put the manufacturing steps in order: Test / Shape / Raw materials / Package.', options: [{ t: 'Test → Shape → Raw → Package', c: false }, { t: 'Raw → Shape → Test → Package', c: true }, { t: 'Package → Shape → Test → Raw', c: false }] },
        { q: 'Which robot joins metal pieces with heat?', options: [{ t: 'Painting robot', c: false }, { t: 'Welding robot', c: true }] },
        { q: 'How is 3D printing different from an assembly line?', options: [{ t: '3D printing uses more workers', c: false }, { t: '3D printing builds one custom object layer by layer', c: true }, { t: '3D printing only makes plastic toys', c: false }] },
        { q: 'What is the most important goal of quality control?', options: [{ t: 'Reduce factory costs', c: false }, { t: 'Make sure every product works perfectly', c: true }, { t: 'Make factories run faster', c: false }] },
      ],
    },
  ],
};

export const CYBER_COURSE = {
  id: 'cyber',
  title: 'Digital Defender',
  emoji: '🛡️',
  badge: { name: 'Cyber Shield', color: '#60a5fa', icon: '🛡️' },
  credential: 'Cybersecurity Fundamentals',
  estimateMin: 30,
  xpAvailable: 450,
  hearts: true,
  nodes: [
    {
      id: 'hacker-think',
      title: 'How Hackers Think',
      emoji: '🕵️',
      kind: 'cards',
      illustration: { emoji: '🕵️💻🔓', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Know their tricks to stop them',
      body: [
        'Hackers try to break into computers and accounts without permission.',
        'They don\'t always use fancy technology. Many hackers use TRICKS to fool people.',
        'A good defender knows how hackers think. If you know their tricks — you can stop them!',
      ],
      cards: [
        { title: 'Social Engineering 🎭', bullets: ['The most common hacking trick', 'Tricking a PERSON, not a computer', 'Example: Pretending to be your school', 'They ask for your login by email'] },
        { title: 'Brute Force 🔨', bullets: ['Try every possible password', 'Short password "cat" = cracked in 1 second', 'Long password = takes centuries', 'Length matters more than tricks'] },
        { title: 'Common Weak Passwords 🚫', bullets: ['"password123" — used by millions', '"123456" — the most common ever', '"qwerty" — first 6 keys on a keyboard', 'If YOUR password is here — change it NOW!'] },
      ],
      quiz: [
        { q: 'What is social engineering?', options: [{ t: 'Building social media apps', c: false }, { t: 'Tricking people into giving up their info', c: true }, { t: 'A type of computer program', c: false }] },
        { q: 'Why is a short password easy for hackers?', options: [{ t: 'Hackers know everyone\'s name', c: false }, { t: 'Few combinations — computers try them all in seconds', c: true }, { t: 'Short passwords contain viruses', c: false }] },
        { q: 'Which password is the WEAKEST?', options: [{ t: 'Mx$9kL!2pQ', c: false }, { t: 'BlueSkyDancer47!', c: false }, { t: 'password123', c: true }] },
      ],
    },
    {
      id: 'phishing',
      title: 'Spotting Phishing Tricks',
      emoji: '🎣',
      kind: 'match',
      illustration: { emoji: '🎣📧🚩', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'Don\'t take the bait',
      body: [
        'Phishing is when someone tries to trick you into giving them your password.',
        'They send fake emails or messages that look real. They pretend to be from schools, banks, or trusted companies.',
        'The word "phishing" sounds like "fishing." The hacker throws out a hook — a fake message. Then they wait to see if you bite!',
      ],
      cards: [
        { title: 'Red Flags to Watch 🚩', bullets: ['Creates urgency: "Act NOW!"', 'Asks for password by email', 'Link looks almost right: gooogle.com', 'Free! Too good to be true offers'] },
        { title: 'Fake vs Real Senders 📧', bullets: ['Real: teacher@yourschool.edu', 'FAKE: support@goog1e.com (the 1)', 'FAKE: prizes@free-winner123.net', 'Always check the sender address'] },
      ],
      match: {
        title: 'Real or Fake?',
        pairs: [
          { left: 'URGENT! Account deletes in 24 hours', right: 'FAKE — urgency trick' },
          { left: 'Homework attached as PDF (from teacher)', right: 'REAL — safe' },
          { left: 'YOU WON $1,000! Enter your info', right: 'FAKE — too good to be true' },
        ],
      },
      quiz: [
        { q: 'What is phishing?', options: [{ t: 'A computer virus', c: false }, { t: 'Tricking someone into giving up personal info', c: true }, { t: 'A type of strong password', c: false }] },
        { q: 'Which is a warning sign of a phishing email?', options: [{ t: 'It comes from a teacher\'s school email', c: false }, { t: 'It says ACT NOW or lose your account!', c: true }, { t: 'It mentions homework', c: false }] },
        { q: 'The link gooogle.com vs google.com — what is this called?', options: [{ t: 'Brute force', c: false }, { t: 'A fake link that looks almost real', c: true }, { t: 'Social media hacking', c: false }] },
      ],
    },
    {
      id: 'passwords',
      title: 'Password Power-Ups',
      emoji: '🔑',
      kind: 'meter',
      illustration: { emoji: '🔑💪🔒', bg: 'linear-gradient(160deg,#0f2744 0%,#60a5fa 100%)' },
      subtitle: 'Build a vault, not a paper lock',
      body: [
        'A strong password is your first line of defense. It\'s like a lock on your digital front door.',
        'A weak password leaves that door wide open. A strong password is like a vault!',
      ],
      meter: {
        title: 'Test each password below',
        intro: 'Tap each row to see how long a hacker would take to crack it.',
        examples: [
          { pw: 'cat', strength: 0, crack: 'Cracked in 1 second!' },
          { pw: 'Cat123', strength: 1, crack: 'Cracked in 2 minutes' },
          { pw: 'CatOnTheRoof', strength: 2, crack: 'Cracked in 3 days' },
          { pw: 'C@tOnTh3R00f!42', strength: 4, crack: 'Takes 15 centuries!' },
        ],
      },
      quiz: [
        { q: 'Which makes a password stronger?', options: [{ t: 'Using your pet\'s name', c: false }, { t: '12+ characters with numbers and symbols', c: true }, { t: 'Using the same one everywhere', c: false }] },
        { q: 'Why is "ILovePizza2025!" a good password?', options: [{ t: 'It has the word "love"', c: false }, { t: 'Long, has numbers, easy to remember', c: true }, { t: 'It has the year', c: false }] },
        { q: 'What should you NEVER put in a password?', options: [{ t: 'Symbols and numbers', c: false }, { t: 'Your real name and birthday', c: true }, { t: 'Capital letters', c: false }] },
      ],
    },
    {
      id: 'safe-habits',
      title: 'Staying Safe Online Every Day',
      emoji: '🛡️',
      kind: 'cards',
      illustration: { emoji: '🔐📱🔄', bg: 'linear-gradient(160deg,#0f2744 0%,#60a5fa 100%)' },
      subtitle: 'Habits that protect you daily',
      body: [
        'Being a Digital Defender is more than just having a strong password.',
        'It\'s about smart habits every single day. These habits protect YOU and the people you care about.',
      ],
      cards: [
        { title: 'Two-Factor Authentication 🔐', bullets: ['Two ways to prove it\'s really you', 'Step 1: Enter your password', 'Step 2: Enter a code from your phone', 'Even if hackers steal your password — they can\'t get in!'] },
        { title: 'Public Wi-Fi Warning ⚠️', bullets: ['Free Wi-Fi = hackers can watch your data', 'Never log in to banks on coffee shop Wi-Fi', 'Use your phone hotspot instead when possible'] },
        { title: 'Software Updates Matter 🔄', bullets: ['"Update available" = weak spot patched', 'Hackers know old software\'s holes', 'Every update makes you safer'] },
        { title: 'Think Before You Share 🤔', bullets: ['Online = potentially forever', 'Photos, videos, personal details linger', 'Protect your future self today'] },
      ],
      quiz: [
        { q: 'What does two-factor authentication do?', options: [{ t: 'Changes your password automatically', c: false }, { t: 'Requires two ways to prove it\'s you', c: true }, { t: 'Blocks all hackers forever', c: false }] },
        { q: 'Why should you update your software?', options: [{ t: 'To get new games', c: false }, { t: 'Updates fix security weak spots hackers exploit', c: true }, { t: 'Updates make your phone look better', c: false }] },
        { q: 'Why be careful what you share online?', options: [{ t: 'No one will see it anyway', c: false }, { t: 'What you post online can stay there forever', c: true }, { t: 'The internet deletes old posts', c: false }] },
      ],
    },
    {
      id: 'defender-challenge',
      title: 'The Defender Challenge!',
      emoji: '🏆',
      kind: 'celebration',
      illustration: { emoji: '🛡️🏆🔐', bg: 'linear-gradient(160deg,#60a5fa 0%,#0f2744 100%)' },
      subtitle: 'Prove you can protect the system',
      body: [
        'You know how hackers think. You spot phishing tricks. You understand strong passwords. You practice safe habits.',
        'It\'s time for the DEFENDER CHALLENGE!',
      ],
      quiz: [
        { q: 'A friend asks for your password. You:', options: [{ t: 'Give it — they\'re trustworthy', c: false }, { t: 'Never share passwords with anyone', c: true }, { t: 'Give a fake one', c: false }] },
        { q: 'You get an email saying your account expires in 1 hour. You:', options: [{ t: 'Click the link immediately', c: false }, { t: 'Check the sender and report if suspicious', c: true }, { t: 'Delete your account', c: false }] },
        { q: 'What does 2FA stand for?', options: [{ t: 'Two-Firewall Access', c: false }, { t: 'Two-Factor Authentication', c: true }, { t: 'Triple-Fast Action', c: false }] },
        { q: 'What makes a password strong?', options: [{ t: 'Using your pet\'s name', c: false }, { t: '12+ characters with numbers and symbols', c: true }, { t: 'Using the same one everywhere', c: false }] },
        { q: 'Best move if your device says "Update available"?', options: [{ t: 'Remind me later, forever', c: false }, { t: 'Update now — patches security holes', c: true }, { t: 'Turn off updates entirely', c: false }] },
      ],
    },
  ],
};

export const SPARK_COURSE = {
  id: 'spark',
  title: 'Spark Academy',
  emoji: '⚡',
  badge: { name: 'Spark Pro', color: '#facc15', icon: '⚡' },
  credential: 'Electrical Fundamentals',
  estimateMin: 35,
  xpAvailable: 500,
  hearts: true,
  nodes: [
    {
      id: 'electricity',
      title: 'What is Electricity, Really?',
      emoji: '⚡',
      kind: 'cards',
      atom: true,
      illustration: { emoji: '⚛️⚡🔌', bg: 'linear-gradient(160deg,#0f2744 0%,#f59e0b 100%)' },
      subtitle: 'The flow of tiny particles',
      body: [
        'Everything around you is made of tiny particles called atoms.',
        'Inside every atom, even tinier particles called electrons move around.',
        'When electrons flow from one place to another — that flowing IS electricity!',
      ],
      cards: [
        { title: 'What Electricity Powers 💡', bullets: ['Your lights and fans', 'Your phone and tablet', 'Your fridge and stove', 'Travels through wires in your walls'] },
        { title: 'Voltage vs. Current 💧', bullets: ['Think of it like water in a hose', 'VOLTAGE = the water pressure', 'CURRENT = how much water flows', 'Both matter — just like water needs pressure AND flow'] },
        { title: 'AC vs. DC 🔌', bullets: ['AC = Alternating Current (wall outlets)', 'DC = Direct Current (batteries)', 'Your phone charger converts AC → DC', 'Different jobs, different currents'] },
      ],
      quiz: [
        { q: 'What is electricity?', options: [{ t: 'Light from the sun', c: false }, { t: 'The flow of electrons from atom to atom', c: true }, { t: 'A type of magnet', c: false }] },
        { q: 'What does VOLTAGE represent?', options: [{ t: 'The amount of electricity used', c: false }, { t: 'The pressure behind the electricity flow', c: true }, { t: 'The type of wire used', c: false }] },
        { q: 'What type of electricity do batteries make?', options: [{ t: 'AC — Alternating Current', c: false }, { t: 'DC — Direct Current', c: true }, { t: 'Solar Current', c: false }] },
      ],
    },
    {
      id: 'circuits',
      title: 'Reading Circuit Diagrams',
      emoji: '🗺️',
      kind: 'cards',
      illustration: { emoji: '🔋💡🔘', bg: 'linear-gradient(160deg,#0f2744 0%,#1e3a5f 100%)' },
      subtitle: 'The universal language of electricity',
      body: [
        'Electricians and engineers use special drawings called circuit diagrams to plan their work.',
        'These use simple symbols instead of drawing real wires and parts.',
        'Once you learn the symbols — you can read any circuit in the world!',
      ],
      cards: [
        { title: 'Battery ⚡', bullets: ['Symbol: two parallel lines', 'Long line = positive (+)', 'Short line = negative (−)', 'Where electricity starts'] },
        { title: 'Light Bulb 💡', bullets: ['Symbol: circle with X inside', 'Where electricity does WORK', 'Turns electricity into light', 'The bulb "lights up" when powered'] },
        { title: 'Switch 🔘', bullets: ['Symbol: line with a hinge gap', 'OPEN = no flow (off)', 'CLOSED = electricity flows (on)', 'Controls the circuit'] },
        { title: 'Wire ——', bullets: ['Any straight line in the diagram', 'Connects everything together', 'Where lines connect = electricity can travel', 'The circulatory system of a circuit'] },
      ],
      quiz: [
        { q: 'In a circuit diagram, a straight line represents ___', options: [{ t: 'A battery', c: false }, { t: 'A light bulb', c: false }, { t: 'A wire', c: true }] },
        { q: 'What happens when a switch is OPEN?', options: [{ t: 'Electricity flows freely', c: false }, { t: 'Electricity cannot flow', c: true }, { t: 'The bulb gets brighter', c: false }] },
        { q: 'A circuit has a battery, a closed switch, and a bulb. What happens?', options: [{ t: 'Nothing happens', c: false }, { t: 'The bulb lights up', c: true }, { t: 'The battery dies immediately', c: false }] },
      ],
    },
    {
      id: 'wiring',
      title: 'Wiring Things Safely',
      emoji: '🔧',
      kind: 'cards',
      illustration: { emoji: '🔧🚨⚠️', bg: 'linear-gradient(160deg,#facc15 0%,#0f2744 100%)' },
      subtitle: 'Safety rules protect lives',
      body: [
        'Electricity is very useful. But it can also be very dangerous.',
        'Electricians stay safe because they know the rules. Learning safety rules NOW protects you for life!',
      ],
      cards: [
        { title: 'Always Turn Off Power First 🔴', bullets: ['Turn off at the breaker', 'Verify with a tester', 'Never assume — always confirm', 'Measure twice, work once'] },
        { title: 'Water + Electricity = Danger ⚠️', bullets: ['Water conducts electricity', 'Small drops near live wires = shock or fire', 'Keep drinks away from all electronics'] },
        { title: 'Use the Right Wire 🔌', bullets: ['Wires come in different thicknesses', 'Too thin = overheating + fire risk', 'Electricians follow exact specs'] },
        { title: 'Respect the Breaker Box 🏠', bullets: ['Controls all your home\'s electricity', 'Trips when overloaded — prevents fires', 'It\'s your home\'s safety net'] },
      ],
      quiz: [
        { q: 'Before working on any wiring, you must:', options: [{ t: 'Put on rubber gloves and start', c: false }, { t: 'Turn off power at the breaker AND verify with a tester', c: true }, { t: 'Ask a friend to watch', c: false }] },
        { q: 'Why use the correct thickness of wire?', options: [{ t: 'Thinner wires look neater', c: false }, { t: 'Too-thin wires overheat and can start fires', c: true }, { t: 'Thicker wires cost less', c: false }] },
        { q: 'What does a circuit breaker do?', options: [{ t: 'Makes electricity go faster', c: false }, { t: 'Cuts power when overloaded to prevent fires', c: true }, { t: 'Stores extra electricity for later', c: false }] },
      ],
    },
    {
      id: 'power-journey',
      title: 'How Power Gets to Your Home',
      emoji: '🏠',
      kind: 'journey',
      illustration: { emoji: '⚡🏗️🏭🏠', bg: 'linear-gradient(160deg,#0f2744 0%,#facc15 100%)' },
      subtitle: 'From plant to plug',
      body: [
        'Have you ever wondered where the electricity in your wall comes from?',
        'It travels a LONG way to reach you! Power plants make it. Towers carry it. Transformers change it. Then it reaches you.',
      ],
      journey: {
        title: 'The Power Journey',
        stops: [
          { icon: '⚡', title: 'Power Plant', text: 'Fuel, sun, wind, or water spins giant generators to make electricity.' },
          { icon: '🏗️', title: 'High-Voltage Lines', text: 'Very high voltage (up to 500,000V) loses less energy over long distance.' },
          { icon: '🏭', title: 'Substation', text: 'Steps DOWN the voltage to a safer level using transformers.' },
          { icon: '🔵', title: 'Neighborhood Transformer', text: 'Green or gray box near your home. Steps voltage down to 120V.' },
          { icon: '🏠', title: 'Your Home', text: 'Enters through a meter, then to your breaker panel, then to every outlet!' },
        ],
      },
      quiz: [
        { q: 'Why is electricity sent at high voltage over long distances?', options: [{ t: 'So it travels faster', c: false }, { t: 'High voltage loses less energy over distance', c: true }, { t: 'It\'s safer for line workers', c: false }] },
        { q: 'What does a transformer do?', options: [{ t: 'Creates electricity from nothing', c: false }, { t: 'Steps voltage up or down', c: true }, { t: 'Stores electricity underground', c: false }] },
        { q: 'What does the meter on your home measure?', options: [{ t: 'The voltage coming in', c: false }, { t: 'How much electricity your home uses', c: true }, { t: 'Whether the breaker is on', c: false }] },
      ],
    },
    {
      id: 'spark-challenge',
      title: 'The Spark Challenge!',
      emoji: '🏆',
      kind: 'celebration',
      illustration: { emoji: '⚡🏆🔧', bg: 'linear-gradient(160deg,#facc15 0%,#f97316 100%)' },
      subtitle: 'Prove you can be an electrician',
      body: [
        'You know what electricity is. You can read circuit diagrams. You know the safety rules. You understand how power reaches your home.',
        'Prove you have what it takes to be an electrician!',
      ],
      quiz: [
        { q: 'Put the power journey in correct order: Substation / Plant / Home / Lines / Transformer', options: [{ t: 'Home → Plant → Lines → Sub → Trans', c: false }, { t: 'Plant → Lines → Substation → Transformer → Home', c: true }, { t: 'Transformer → Lines → Plant → Sub → Home', c: false }] },
        { q: 'Voltage is like water ___', options: [{ t: 'Temperature', c: false }, { t: 'Pressure', c: true }, { t: 'Color', c: false }] },
        { q: 'Before touching any wire you must:', options: [{ t: 'Check if it looks okay', c: false }, { t: 'Turn off power AND verify with a tester', c: true }, { t: 'Wear sunglasses', c: false }] },
        { q: 'What type of electricity comes from your wall outlet?', options: [{ t: 'DC — Direct Current', c: false }, { t: 'AC — Alternating Current', c: true }, { t: 'Solar Current', c: false }] },
        { q: 'An electrician uses a too-thin wire. What could happen?', options: [{ t: 'Nothing — all wires work the same', c: false }, { t: 'The wire overheats and could start a fire', c: true }, { t: 'The circuit just runs faster', c: false }] },
      ],
    },
  ],
};

export const CODE_COURSE = {
  id: 'code',
  title: 'Code Quest',
  emoji: '💻',
  badge: { name: 'Code Builder', color: '#a78bfa', icon: '💻' },
  credential: 'Web Development Fundamentals',
  estimateMin: 35,
  xpAvailable: 500,
  hearts: true,
  nodes: [
    {
      id: 'what-is-code',
      title: 'What is Code?',
      emoji: '💻',
      kind: 'cards',
      illustration: { emoji: '💻⌨️🌐', bg: 'linear-gradient(160deg,#0f2744 0%,#a78bfa 100%)' },
      subtitle: 'How humans talk to computers',
      body: [
        'Every app, website, and video game was built by someone writing instructions called code.',
        'Code is how humans talk to computers. Computers are powerful — but they need very specific instructions.',
        'A coder\'s job is to write those instructions!',
      ],
      cards: [
        { title: 'Code = Instructions 📝', bullets: ['Step-by-step instructions', 'Computers follow them exactly', 'A tiny typo can break everything!', 'Fixing typos is called debugging'] },
        { title: 'Programming Languages 🌐', bullets: ['HTML — page STRUCTURE', 'CSS — how it LOOKS', 'JavaScript — makes it INTERACTIVE', 'Python — great for AI and data'] },
        { title: 'Computers Are Literal 🤖', bullets: ['They do EXACTLY what you type', 'Not what you meant', 'One character off = broken code', 'Careful checking is the coder\'s superpower'] },
      ],
      quiz: [
        { q: 'What is code?', options: [{ t: 'A secret spy message', c: false }, { t: 'Instructions a computer can understand', c: true }, { t: 'A type of computer chip', c: false }] },
        { q: 'Which coding language builds the STRUCTURE of a website?', options: [{ t: 'Python', c: false }, { t: 'CSS', c: false }, { t: 'HTML', c: true }] },
        { q: 'Why does a tiny typo break code?', options: [{ t: 'Computers guess what you meant', c: false }, { t: 'Computers do exactly what you type — even when wrong', c: true }, { t: 'Most code needs no exact spelling', c: false }] },
      ],
    },
    {
      id: 'html-css',
      title: 'HTML & CSS Basics',
      emoji: '🎨',
      kind: 'match',
      illustration: { emoji: '🌐🎨🖌️', bg: 'linear-gradient(160deg,#0f2744 0%,#a78bfa 100%)' },
      subtitle: 'Structure + style = website',
      body: [
        'HTML uses TAGS to tell the browser what each piece of content is.',
        'CSS gives your page its STYLE — colors, sizes, layout.',
        'Together, HTML + CSS make every website you\'ve ever seen!',
      ],
      cards: [
        { title: '<h1> Heading Tag', bullets: ['Makes big bold headings', 'Open with <h1> — close with </h1>', 'One h1 per page is best'] },
        { title: '<p> Paragraph Tag', bullets: ['Creates a paragraph of text', 'Browsers add space above and below', 'Used for regular reading text'] },
        { title: 'CSS color:', bullets: ['h1 { color: orange; }', 'Changes text color', 'Use names (red) or codes (#ff0000)'] },
        { title: 'CSS font-size:', bullets: ['p { font-size: 18px; }', 'px = pixels (dots on screen)', 'Bigger number = bigger text'] },
      ],
      match: {
        title: 'Match Tag / Rule to Job!',
        pairs: [
          { left: 'Big page heading', right: '<h1>' },
          { left: 'A paragraph of text', right: '<p>' },
          { left: 'Change text color', right: 'color:' },
        ],
      },
      quiz: [
        { q: 'What does HTML stand for?', options: [{ t: 'High Tech Machine Language', c: false }, { t: 'HyperText Markup Language', c: true }, { t: 'Home Tool Making Language', c: false }] },
        { q: 'What does CSS do?', options: [{ t: 'Creates the structure of a webpage', c: false }, { t: 'Controls how a webpage looks', c: true }, { t: 'Makes webpages interactive', c: false }] },
        { q: 'Which CSS property changes text color?', options: [{ t: 'background-color:', c: false }, { t: 'font-size:', c: false }, { t: 'color:', c: true }] },
      ],
    },
    {
      id: 'code-final',
      title: 'Build Your First Page!',
      emoji: '🌐',
      kind: 'drag',
      illustration: { emoji: '🌐🧱📄', bg: 'linear-gradient(160deg,#0f2744 0%,#a78bfa 100%)' },
      subtitle: 'Drag or tap the tags in the right order',
      body: [
        'Time to build! Drag or tap each HTML tag in the correct order to assemble a working page.',
        'The tags need to open and close in the right sequence — just like nested folders.',
      ],
      drag: {
        title: 'Assemble your first webpage',
        subtitle: 'Drag a tile into the box — or tap it. Wrong choice = gentle shake.',
        tiles: [
          { id: 't1', label: '<html>' },
          { id: 't2', label: '<head>' },
          { id: 't3', label: '<title>My Page</title>' },
          { id: 't4', label: '</head>' },
          { id: 't5', label: '<body>' },
          { id: 't6', label: '<h1>Hello!</h1>' },
          { id: 't7', label: '<p>My site</p>' },
          { id: 't8', label: '</body>' },
          { id: 't9', label: '</html>' },
        ],
        order: ['t1', 't2', 't3', 't4', 't5', 't6', 't7', 't8', 't9'],
        preview: { h1: 'Hello!', p: 'My site' },
      },
      quiz: [
        { q: 'Which tag must open before <body>?', options: [{ t: '<p>', c: false }, { t: '<html>', c: true }, { t: '<a>', c: false }] },
        { q: 'What does <h1>...</h1> create?', options: [{ t: 'A link', c: false }, { t: 'A big page heading', c: true }, { t: 'An image', c: false }] },
        { q: 'Every open tag <p> needs...', options: [{ t: 'a closing </p>', c: true }, { t: 'a comma', c: false }, { t: 'nothing extra', c: false }] },
      ],
    },
    {
      id: 'quest-challenge',
      title: 'The Code Quest Challenge!',
      emoji: '🏆',
      kind: 'celebration',
      illustration: { emoji: '💻🏆🚀', bg: 'linear-gradient(160deg,#a78bfa 0%,#0f2744 100%)' },
      subtitle: 'Prove you can build for the web',
      body: [
        'You know what code is. You can name the languages. You understand HTML tags and CSS rules. You even built your first page!',
        'One final challenge to earn your Code Builder badge.',
      ],
      quiz: [
        { q: 'Which tag creates a big heading?', options: [{ t: '<p>', c: false }, { t: '<a>', c: false }, { t: '<h1>', c: true }] },
        { q: 'What is the correct CSS pattern?', options: [{ t: 'value { selector: property; }', c: false }, { t: 'selector { property: value; }', c: true }, { t: 'property { value: selector; }', c: false }] },
        { q: 'A tiny typo breaks your code. What do coders call fixing it?', options: [{ t: 'Refactoring', c: false }, { t: 'Debugging', c: true }, { t: 'Deleting', c: false }] },
        { q: 'Which trio builds modern websites?', options: [{ t: 'HTML + CSS + JavaScript', c: true }, { t: 'Python + Java + Ruby', c: false }, { t: 'Word + Excel + PowerPoint', c: false }] },
        { q: 'Which CSS property changes text color?', options: [{ t: 'background-color:', c: false }, { t: 'font-size:', c: false }, { t: 'color:', c: true }] },
      ],
    },
  ],
};

Object.assign(COURSE_BY_ID, {
  maker: MAKER_COURSE,
  cyber: CYBER_COURSE,
  spark: SPARK_COURSE,
  code: CODE_COURSE,
});

/* Full 6-node path order for map rendering.
   All 4 previously-locked courses now have real content, but stay
   `kind: 'locked'` in the path — the home screen upgrades them to
   `open` once the unlock rule passes. */
export const YOUTH_PATH = [
  { id: 'solar', kind: 'open' },
  { id: 'robot', kind: 'open' },
  { id: 'maker', kind: 'locked' },
  { id: 'cyber', kind: 'locked' },
  { id: 'spark', kind: 'locked' },
  { id: 'code', kind: 'locked' },
];

/* Unlock rules — checked at home screen render + on preview screen. */
export const UNLOCK_RULES = {
  maker: (done) => !!done.solar,
  cyber: (done) => !!done.robot,
  spark: (done) => !!done.maker,
  code: (done) => !!done.spark && !!done.cyber,
};

export const CHEER_LINES = ['Right on!', 'You got it!', 'Excellent!', 'That\'s correct!', 'Nice work!'];

/* ==========================================================
   Unit Boss Challenges — 3-question gates between chapters.
   Key format: 'ch{from}-ch{to}' matching CHAPTERS indices.
   ========================================================== */
export const BOSS_QUIZZES = {
  'ch1-ch2': {
    title: 'Unit 1 Boss Challenge',
    subtitle: 'Prove your skills to unlock Level 2!',
    emoji: '⚡',
    questions: [
      {
        q: 'Your cabin has a small, shady roof. Which solar panel should you choose?',
        options: [
          { t: 'Polycrystalline — it costs less', c: false },
          { t: 'Monocrystalline — made for small shady spaces', c: true },
          { t: 'Either one works the same', c: false },
          { t: 'Neither — use a generator instead', c: false },
        ],
        explanation: 'Monocrystalline panels are more efficient in small spaces with partial shade.',
      },
      {
        q: "Marcus's robot dog keeps bumping into walls. Which part should he check first?",
        options: [
          { t: 'The battery power', c: false },
          { t: 'The wheel motors', c: false },
          { t: 'The sensors', c: true },
          { t: 'The controller brain', c: false },
        ],
        explanation: 'Sensors detect the environment — a failed bump sensor causes the robot to miss walls.',
      },
      {
        q: 'A robot reads sensors, makes a plan, then moves. What is this loop called?',
        options: [
          { t: 'Plan → Move → Stop', c: false },
          { t: 'Sense → Plan → Act', c: true },
          { t: 'Act → Sense → Plan', c: false },
          { t: 'Read → Think → Rest', c: false },
        ],
        explanation: 'Sense-Plan-Act is the core loop behind every robot decision.',
      },
    ],
  },
  'ch2-ch3': {
    title: 'Unit 2 Boss Challenge',
    subtitle: 'Prove your skills to unlock the Expert Tier!',
    emoji: '🔥',
    questions: [
      {
        q: "Henry Ford's assembly line improved manufacturing by:",
        options: [
          { t: 'Having one worker build the whole car alone', c: false },
          { t: 'Letting each worker focus on just one task', c: true },
          { t: 'Building cars only at night for speed', c: false },
          { t: 'Replacing all workers with robots', c: false },
        ],
        explanation: 'Each worker doing one focused task dramatically cut car build time.',
      },
      {
        q: 'You get an email: "ACT NOW — your account expires in 1 hour!" What is this?',
        options: [
          { t: 'A normal reminder from your school', c: false },
          { t: 'A phishing trick using urgency', c: true },
          { t: 'A software update notification', c: false },
          { t: 'A two-factor authentication code', c: false },
        ],
        explanation: 'Creating panic with "ACT NOW" is a classic phishing red flag.',
      },
      {
        q: 'A factory makes 500 toys and finds 10 have broken wheels. What should happen next?',
        options: [
          { t: "Ship all 500 — it's only 10", c: false },
          { t: 'Throw all 500 away to be safe', c: false },
          { t: 'Fix the broken ones before shipping', c: true },
          { t: 'Sell the broken ones at a discount', c: false },
        ],
        explanation: 'Quality control means fixing defective products before they reach customers.',
      },
    ],
  },
};
